import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/categories";
import { pillarArticles } from "@/mocks/articles";

const categoryMap: Record<string, string> = {
  "Guide": "category.guide",
  "Review": "category.review",
  "Safety": "category.safety",
  "Shipping": "category.shipping",
  "Warehouse": "category.warehouse",
  "QC": "category.qc",
  "Coupons": "category.coupons",
  "Comparison": "category.comparison",
  "Beginners": "category.beginners",
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const detected = i18n.language || "en";
  const lang = (detected.split("-")[0] as "en" | "fr" | "da" | "de") || "en";
  const article = pillarArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    setOpenFaq(0);
  }, [slug]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="mx-auto max-w-[1280px] px-4 md:px-6 py-24 text-center">
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
              {t("article.notFound")}
            </h1>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              {t("article.backHome")}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  function scrollTo(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  }

  const categoryKey = categoryMap[article.category] || "category.guide";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <article className="mx-auto max-w-[1280px] px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              {t("nav.home")}
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            <span className="text-foreground-700">{article.title[lang]}</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <span className="inline-flex items-center rounded-full bg-primary-100 text-primary-700 px-3 py-1 text-xs font-semibold">
                {t(categoryKey)}
              </span>
              <h1 className="mt-4 font-heading text-3xl md:text-4xl font-semibold leading-tight text-foreground-950">
                {article.title[lang]}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-foreground-500">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                  {article.readTime}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  Updated 2026
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-information-line" />
                  </span>
                  Independent editorial resource
                </span>
              </div>

              <div className="mt-6 relative w-full h-56 md:h-80 overflow-hidden rounded-lg">
                <img
                  src={article.image}
                  alt={`${article.title[lang]} — AllChinaBuy Spreadsheet`}
                  title={`${article.title[lang]} AllChinaBuy Spreadsheet`}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <p className="mt-6 text-base md:text-lg text-foreground-700 leading-relaxed">
                {article.intro[lang]}
              </p>

              {article.sections.map((section, index) => (
                <section
                  key={index}
                  id={`section-${index}`}
                  className="mt-10"
                >
                  <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                    {section.title[lang]}
                  </h2>
                  <div className="mt-4 text-foreground-700 leading-relaxed whitespace-pre-line">
                    {section.body[lang]}
                  </div>
                </section>
              ))}

              <section id="article-faq" className="mt-12">
                <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-950">
                  {t("article.faqTitle")}
                </h2>
                <div className="mt-5 space-y-3">
                  {article.faq.map((item, index) => {
                    const isOpen = openFaq === index;
                    return (
                      <div
                        key={index}
                        className="rounded-lg border border-background-200 bg-background-50 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={() => setOpenFaq(isOpen ? null : index)}
                          className="flex w-full items-center justify-between px-5 py-4 text-left cursor-pointer"
                          aria-expanded={isOpen}
                        >
                          <span className="font-medium text-foreground-950">
                            {item.q[lang]}
                          </span>
                          <span className="w-5 h-5 flex items-center justify-center text-foreground-500 shrink-0">
                            <i
                              className={`ri-add-line transition-transform ${
                                isOpen ? "rotate-45" : ""
                              }`}
                            />
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                            {item.a[lang]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>

              <section
                id="conclusion"
                className="mt-12 rounded-lg border border-background-200 bg-secondary-50 p-6 md:p-8"
              >
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  {t("article.summary")}
                </h2>
                <p className="mt-3 text-foreground-700 leading-relaxed">
                  {article.conclusion[lang]}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                  >
                    {t("hero.ctaSecondary")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line" />
                    </span>
                  </a>
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 whitespace-nowrap cursor-pointer"
                  >
                    {t("article.backHome")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line" />
                    </span>
                  </Link>
                </div>
              </section>
            </div>

            <aside className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">
                    {t("article.tocTitle")}
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {article.sections.map((section, index) => (
                      <li key={index}>
                        <button
                          type="button"
                          onClick={() => scrollTo(`section-${index}`)}
                          className="text-left text-sm text-foreground-600 hover:text-primary-700 cursor-pointer"
                        >
                          {section.title[lang]}
                        </button>
                      </li>
                    ))}
                    <li>
                      <button
                        type="button"
                        onClick={() => scrollTo("article-faq")}
                        className="text-left text-sm text-foreground-600 hover:text-primary-700 cursor-pointer"
                      >
                        {t("article.faqTitle")}
                      </button>
                    </li>
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">
                    {t("article.relatedTitle")}
                  </h3>
                  <ul className="mt-3 space-y-2">
                    {pillarArticles
                      .filter((a) => a.slug !== article.slug)
                      .slice(0, 5)
                      .map((a) => (
                        <li key={a.slug}>
                          <Link
                            to={`/article/${a.slug}`}
                            className="text-sm text-foreground-600 hover:text-primary-700 cursor-pointer"
                          >
                            {a.title[lang]}
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="font-heading text-sm font-semibold text-foreground-950">
                    {t("nav.home")}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600">
                    {t("article.backHint")}
                  </p>
                  <Link
                    to="/"
                    className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer"
                  >
                    {t("article.backHome")}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line" />
                    </span>
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}